import { useQuery } from '@tanstack/react-query';
import WashdayClientSingleton from '../../services/WashdayClientSingleton';

export type InvoiceModuleCapabilities = {
  enabled?: boolean;
  country?: {
    code?: string | null;
    supported?: boolean;
    standard?: string | null;
    [key: string]: unknown;
  };
  provider?: {
    provider?: string | null;
    status?: string;
    [key: string]: unknown;
  };
  issuerProfile?: {
    status?: string;
    [key: string]: unknown;
  };
  permissions?: {
    canView?: boolean;
    canMutate?: boolean;
    [key: string]: unknown;
  };
  plan?: {
    status?: string;
    allowed?: boolean;
    [key: string]: unknown;
  };
  legacy?: {
    canUseCFDIInvoicing?: boolean;
    canUseInvoiceModule?: boolean;
    [key: string]: unknown;
  };
  reasons?: string[];
  [key: string]: unknown;
};

type InvoiceModuleCapabilitiesResponse = {
  data?: {
    data?: InvoiceModuleCapabilities | null;
  };
};

type InvoiceModuleCapabilitiesClient = {
  invoiceModule?: {
    getCapabilities?: () => Promise<InvoiceModuleCapabilitiesResponse>;
  };
};

type UseInvoiceModuleCapabilitiesOptions = {
  enabled?: boolean;
};

export type UseInvoiceModuleCapabilitiesResult = {
  capabilities: InvoiceModuleCapabilities | null;
  isLoading: boolean;
  isFetching: boolean;
  isError: boolean;
  error: unknown;
  canViewCFDI: boolean;
  canMutateCFDI: boolean;
  canShowCFDI: boolean;
  canShowCustomerCFDIFields: boolean;
  reasonCodes: string[];
};

export const invoiceModuleCapabilitiesQueryKey = ['invoice-module-capabilities'];

const CFDI_MUTATION_PROVIDER_STATUSES = ['active', 'test'];

const normalizeCapabilitiesResponse = (
  response: InvoiceModuleCapabilitiesResponse
): InvoiceModuleCapabilities | null => {
  return response?.data?.data ?? null;
};

export const getInvoiceModuleCFDIAccess = (
  capabilities?: InvoiceModuleCapabilities | null
): Pick<
  UseInvoiceModuleCapabilitiesResult,
  'canViewCFDI' | 'canMutateCFDI' | 'canShowCFDI' | 'reasonCodes'
> => {
  const canViewCFDI =
    capabilities?.country?.standard === 'cfdi' &&
    capabilities.country.supported === true &&
    capabilities.permissions?.canView === true &&
    capabilities.plan?.allowed === true &&
    (capabilities.legacy?.canUseInvoiceModule === true ||
      capabilities.legacy?.canUseCFDIInvoicing === true);

  const canMutateCFDI =
    canViewCFDI &&
    capabilities?.permissions?.canMutate === true &&
    CFDI_MUTATION_PROVIDER_STATUSES.includes(capabilities.provider?.status || '') &&
    capabilities.issuerProfile?.status === 'ready';

  return {
    canViewCFDI,
    canMutateCFDI,
    canShowCFDI: canViewCFDI,
    reasonCodes: capabilities?.reasons || []
  };
};

export const getInvoiceModuleCustomerCFDIFieldsAccess = (
  capabilities?: InvoiceModuleCapabilities | null
): boolean => {
  return (
    capabilities?.country?.standard === 'cfdi' &&
    capabilities.country.supported === true &&
    capabilities.permissions?.canView === true &&
    (capabilities.legacy?.canUseInvoiceModule === true ||
      capabilities.legacy?.canUseCFDIInvoicing === true)
  );
};

const fetchInvoiceModuleCapabilities = async (): Promise<InvoiceModuleCapabilities | null> => {
  const client =
    WashdayClientSingleton.getInstance() as unknown as InvoiceModuleCapabilitiesClient;

  if (!client.invoiceModule?.getCapabilities) {
    throw new Error('Invoice module capabilities endpoint is not available in the SDK client');
  }

  const response = await client.invoiceModule.getCapabilities();
  return normalizeCapabilitiesResponse(response);
};

const useInvoiceModuleCapabilities = (
  options: UseInvoiceModuleCapabilitiesOptions = {}
): UseInvoiceModuleCapabilitiesResult => {
  const query = useQuery({
    queryKey: invoiceModuleCapabilitiesQueryKey,
    queryFn: fetchInvoiceModuleCapabilities,
    enabled: options.enabled ?? true,
    retry: false
  });
  const capabilities = query.data ?? null;
  const cfdiAccess = getInvoiceModuleCFDIAccess(capabilities);
  const canShowCustomerCFDIFields =
    getInvoiceModuleCustomerCFDIFieldsAccess(capabilities);

  return {
    capabilities,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    isError: query.isError,
    error: query.error,
    canShowCustomerCFDIFields,
    ...cfdiAccess
  };
};

export default useInvoiceModuleCapabilities;
